/* eslint-disable max-classes-per-file */
// eslint-disable-next-line import/no-extraneous-dependencies
import { FirebaseError } from '@firebase/util';
import { Container, Link as MaterialLink, Typography } from '@material-ui/core';
import * as Sentry from '@sentry/nextjs';
import classNames from 'classnames';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  Auth,
  FacebookAuthProvider,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  UserCredential,
} from 'firebase/auth';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import AlertPopup from '@components/common/alert-popup';
import Figures, { Circle, Image } from '@components/common/figures';
import ResetPasswordForm from '@components/common/reset-password-form';
import SignInForm from '@components/common/sign-in-form';
import SignUpForm from '@components/common/sign-up-form';
import useTypedSelector from '@hooks/use-typed-selector';
import GoogleSVG from '@public/svg/google.svg';
import Toast from '@services/toast';
import { setSignInWithRedirect } from '@store/auth';
import { facebookAuth, googleAuth } from '@store/auth/thunks';
import useStyles from '@views/auth/styles';

interface Props {
  backButton: boolean;
  view: 'signIn' | 'signUp' | 'resetPassword';
}

const AuthView: React.FC<Props> = (props) => {
  const { view, backButton } = props;

  const {
    resettingEmailSent,
    emailToReset,
    passwordResetSuccess,
    isSignInWithRedirect,
    status,
  } = useTypedSelector((state) => state.auth);

  const dispatch = useDispatch();

  const classes = useStyles();

  const router = useRouter();
  const userToken =
    router.query.token instanceof Array
      ? router.query.token[0]
      : router.query.token;

  const circles: Circle[] = [
    {
      background: 'blueGradient',
      classWrapper: classes.circleOne,
    },
    {
      background: 'pink',
      classWrapper: classes.circleTwo,
    },
    {
      background: 'pinkMain',
      classWrapper: classes.circleThree,
    },
    {
      background: 'blueGradient',
      classWrapper: classNames({
        [classes.circleFour]: view === 'resetPassword',
      }),
    },
  ];

  const image: Image[] = [
    {
      src:
        (view === 'resetPassword' &&
          (userToken ? '/png/lock.png' : '/png/magnifying-glass.png')) ||
        '/png/person-with-rocket.png',
      classWrapper:
        (view === 'resetPassword' &&
          (userToken ? classes.figuresLock : classes.magnifyingGlass)) ||
        classes.personWithRocket,
    },
  ];

  const goBack = () => {
    router.back();
  };

  const handleGoogleLogin = (result: UserCredential) => {
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const token = credential?.idToken;
    const { displayName } = result.user;

    if (token && displayName) {
      dispatch(googleAuth({ token, displayName }));
    }
  };

  const handleFacebookLogin = (result: UserCredential) => {
    const credential = FacebookAuthProvider.credentialFromResult(result);
    const token = credential?.accessToken;

    if (token) {
      dispatch(facebookAuth({ token }));
    }
  };

  const handleSocialLoginError =
    (auth: Auth, provider: GoogleAuthProvider | FacebookAuthProvider) =>
    (err: unknown) => {
      const error = err as FirebaseError;

      Sentry.captureException(err);

      if (error.code === 'auth/popup-blocked') {
        dispatch(setSignInWithRedirect('google'));

        signInWithRedirect(auth, provider);
      }
    };

  const startGoogleLogin: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();

    try {
      const provider = new GoogleAuthProvider();
      const auth = getAuth();

      signInWithPopup(auth, provider)
        .then(handleGoogleLogin)
        .catch(handleSocialLoginError(auth, provider));
    } catch (err) {
      const error = err as Error;

      dispatch(setSignInWithRedirect(undefined));
      Sentry.captureException(error);
      Toast.error(`Authentication failed. Message: ${error.message}`);
    }
  };

  const startFacebookLogin: React.MouseEventHandler<HTMLButtonElement> = (
    e,
  ) => {
    e.preventDefault();

    try {
      const provider = new FacebookAuthProvider();
      const auth = getAuth();

      signInWithPopup(auth, provider)
        .then(handleFacebookLogin)
        .catch(handleSocialLoginError(auth, provider));
    } catch (err) {
      const error = err as Error;

      dispatch(setSignInWithRedirect(undefined));
      Sentry.captureException(error);
      Toast.error(`Authentication failed. Message: ${error.message}`);
    }
  };

  useEffect(() => {
    const auth = getAuth();

    if (isSignInWithRedirect) {
      getRedirectResult(auth)
        .then((result) => {
          if (result) {
            if (isSignInWithRedirect === 'google') {
              handleGoogleLogin(result);
            } else {
              handleFacebookLogin(result);
            }
          }
        })
        .catch((err) => {
          const error = err as FirebaseError;

          dispatch(setSignInWithRedirect(undefined));
          Sentry.captureException(error);

          if (error.code === 'auth/web-storage-unsupported') {
            Toast.error(
              'This type of action is not supported in Incognito mode.',
            );
          } else {
            Toast.error(`Authentication failed. Message: ${err.message}`);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <section
      className={classNames(classes.positionRelative, classes.overflowHidden)}
    >
      <Container maxWidth="md">
        <section className={classes.wrapper}>
          <section>
            {backButton && !resettingEmailSent && (
              <MaterialLink
                id="back-button-auth"
                className={classes.linkBack}
                onClick={goBack}
              >
                <i className="icon-arrow-left" /> Back
              </MaterialLink>
            )}
          </section>
          <section
            className={classNames({
              [classes.signUpWrapper]: view === 'signUp',
            })}
          >
            {view === 'signUp' && (
              <AlertPopup
                icon="lock"
                title="Privacy guaranteed"
                description={
                  <>
                    We use the best data security, ensuring your data remains
                    completely confidential - Your information will never be
                    shared or sold.
                  </>
                }
                classWrapper={classes.popup}
                fixedOnTablet
              />
            )}
            <div className={classes.innerContainer}>
              {view === 'signIn' && (
                <SignInForm
                  isLoading={status === 'pending' || !!isSignInWithRedirect}
                />
              )}
              {view === 'signUp' && (
                <SignUpForm
                  isLoading={status === 'pending' || !!isSignInWithRedirect}
                />
              )}
              {view === 'resetPassword' && (
                <ResetPasswordForm
                  resettingEmailSent={resettingEmailSent}
                  passwordResetSuccess={passwordResetSuccess}
                  userToken={userToken}
                  isLoading={status === 'pending'}
                  emailToReset={emailToReset}
                />
              )}
              {(view === 'signIn' || view === 'signUp') && (
                <section>
                  <Typography variant="body2" className={classes.signWith}>
                    Sign in with
                  </Typography>
                  <div className={classes.buttonsWrapper}>
                    <button
                      id="facebook-sign-in"
                      className={classes.socialSignInButton}
                      type="button"
                      onClick={startFacebookLogin}
                      disabled={status === 'pending' || !!isSignInWithRedirect}
                    >
                      <i className="icon-facebook" />
                    </button>
                    <button
                      id="google-sign-in"
                      className={classes.socialSignInButton}
                      type="button"
                      onClick={startGoogleLogin}
                      disabled={status === 'pending' || !!isSignInWithRedirect}
                    >
                      <GoogleSVG />
                    </button>
                  </div>
                </section>
              )}
            </div>
          </section>
          <Figures
            images={image}
            circles={circles}
            classWrapper={classNames({
              [classes.resetPage]: view === 'resetPassword',
            })}
          />
        </section>
      </Container>
    </section>
  );
};

export default AuthView;
